import { useEffect, useRef, useMemo } from 'react';
import { SVG } from '@svgdotjs/svg.js';

import useStore from '../../../store/store';
import { IntersectionCoordinates } from '../../../utility';

const Shadow = ({ x0, y0, x1, y1 }) => {
	const polygonRef = useRef();
	const distance = useStore((state) => state.distance);
	const getUnitFromMeter = useStore((state) => state.getUnitFromMeter);

	useEffect(() => {
		const draw = SVG('#shadows');
		polygonRef.current = draw.polygon().fill('#ef7521').back().opacity('0.3');
		return () => polygonRef.current.remove();
	}, []);

	useEffect(() => {
		const { x, y } = IntersectionCoordinates(
			{ x0, y0 },
			{ x1, y1 },
			{ x: distance + distance * 12.5 / 75 }
		);

		polygonRef.current.plot(`
			${getUnitFromMeter(x1)},0
			${getUnitFromMeter(x1)},${-getUnitFromMeter(y1)}
			${getUnitFromMeter(x)},${-(y === Infinity ? 100000 : (y === -Infinity ? -10000 : getUnitFromMeter(y)))}
			${getUnitFromMeter(x)},0
		`);
	}, [distance, x0, y0, x1, y1]);

	return null;
};

const Shadows = () => {
	const getSoundSourcePosition = useStore((state) => state.getSoundSourcePosition);
	const [x0, y0] = useStore((state) => [0, state.sourceHeight]);
	const [closeBarrierX, closeBarrierY, farBarrierX, farBarrierY] = useStore((state) => (
		Number(state.barrier1.distance) <= Number(state.barrier2.distance) || state.scenario === 'singleBarrier'
			? [state.barrier1.distance, state.barrier1.height, state.barrier2.distance, state.barrier2.height]
			: [state.barrier2.distance, state.barrier2.height, state.barrier1.distance, state.barrier1.height]
	));

	const scenario = useStore((state) => state.scenario);
	const isFarBarrierUnderCloseBarrier = useMemo(() => {
		const { y } = IntersectionCoordinates(
			{ x0, y0 },
			{ x1: closeBarrierX, y1: closeBarrierY },
			{ x: farBarrierX }
		);

		return Number(y) - Number(farBarrierY) > 0;
	}, [x0, y0, closeBarrierX, closeBarrierY, farBarrierX, farBarrierY]);

	useEffect(() => {
		const shadows = SVG('#shadows');
		const { x, y } = getSoundSourcePosition();
		shadows.transform({ translateX: x, translateY: y });
	}, []);

	return (
		<>
			{scenario !== 'noBarriers' && !(scenario === 'building' && !isFarBarrierUnderCloseBarrier) && (
				<Shadow
					x0={x0}
					y0={y0}
					x1={closeBarrierX}
					y1={closeBarrierY}
				/>
			)}
			{(scenario === 'doubleBarrier' || scenario === 'building') && (
				<Shadow
					x0={isFarBarrierUnderCloseBarrier ? closeBarrierX : x0}
					y0={isFarBarrierUnderCloseBarrier ? closeBarrierY : y0}
					x1={farBarrierX}
					y1={farBarrierY}
				/>
			)}
		</>
	);
};

export { Shadows };